import { ProductPageHeader } from '@design-system/product-page-header';
import {
  getLocalStorageData,
  removeLocalStorageData,
} from '@phx-husky/local-storage';
import { graphql } from 'gatsby';
import { kebabCase } from 'lodash';
import React, { FC, useEffect } from 'react';

import {
  Adimo,
  BazaarVoice,
  check3rdPartyAvailability,
  MikMak,
  PriceSpider,
  PriceSpiderModel,
} from '~/3rdPartyIntegrations';
import BodyRender from '~/components/BodyRender';
import { BodyRenderExternalData } from '~/components/BodyRender/models';
import { RedirectsProvider } from '~/context/redirects';
import { Layout } from '~/layout/Layout';
import { productPageHeaderMapper } from '~/mappers/productPageHeader';
import { gtmService } from '~/services/gtm';
import { GTM_LOCALSTORAGE_DATA } from '~/utils/constants';
import withContentstackPreview from '~/utils/livePreview';

import { ProductPageData } from './models';

const BASE_CLASS = 'l2-product-page';

const ProductPage: FC<ProductPageData> = ({
  data: {
    profile,
    contentstackL2ProductPage,
    contentstackHeader,
    contentstackHeaderWithSplitter,
    contentstackFooter,
    contentstackFooterWithSplitter,
    contentstackInfoBar,
    contentstackSettings,
    allContentstackAssets: { nodes: jsonRTEImages },
  },
  pageContext: {
    uid,
    locale,
    seoTitle,
    brand,
    pageTemplate,
    fullPath,
    redirects,
  },
}) => {
  if (typeof window === 'undefined')
    console.info('Building:', JSON.stringify({ uid, locale, seoTitle }));

  const {
    seo,
    body,
    fields: { productData },
    categories,
    openGraph,
  } = contentstackL2ProductPage;
  const {
    bazaarvoiceUrl,
    siteUrl,
    bazaarvoiceProductFieldName,
    priceSpider,
    adimo,
    shoppablePlugin,
    pluginInformationText,
    mikmak,
  } = contentstackSettings;

  const {
    isPriceSpiderAvailable,
    isAdimoAvailable,
    isMikMakV1Available,
    isMikMakV2Available,
  } = check3rdPartyAvailability(shoppablePlugin, priceSpider, adimo, mikmak);
  const mainImagePath = productData.heroImage.url;

  const lastProductIndex = getLocalStorageData(
    GTM_LOCALSTORAGE_DATA.lastProductIndex
  );
  gtmService.emitViewItem(productData, pageTemplate, lastProductIndex);

  useEffect(() => {
    const handleBeforeUnload = () => {
      removeLocalStorageData(GTM_LOCALSTORAGE_DATA.lastProductIndex);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      removeLocalStorageData(GTM_LOCALSTORAGE_DATA.lastProductIndex);
    };
  }, []);

  const categoryClasses = categories.map(
    ({ title }) => `${BASE_CLASS}--${kebabCase(title)}`
  );

  const whereToBuyData = {
    barcodeNumber: productData.barcodeNumber,
    isPriceSpiderAvailable,
    isAdimoAvailable,
    adimoKey: adimo?.key,
    adimoButton: adimo?.buttonText,
    isMikMakV1Available,
    isMikMakV2Available,
    mikmakAppId: mikmak?.appId,
    mikmakWidgetId: mikmak?.widgetId,
    mikmakButton: mikmak?.buttonText,
    shoppableInformationText: pluginInformationText,
  };

  const externalData: BodyRenderExternalData = {
    salsifyExternalData: productData,
    jsonRTEImages,
    whereToBuyData,
  };

  const productPageHeaderData = productData
    ? productPageHeaderMapper({
        productPageHeaderBasic: {
          ...contentstackL2ProductPage,
          productId: productData[bazaarvoiceProductFieldName],
          whereToBuyData,
        },
      })
    : null;

  return (
    <RedirectsProvider redirects={redirects}>
      <Layout
        {...{
          openGraph,
          mainImagePath,
          seo: {
            ...seo,
            additionalMeta: [...seo.additionalMeta],
            pageType: 'og:product',
          },
          headerBasic: contentstackHeader,
          headerWithSplitter: contentstackHeaderWithSplitter,
          headerSplitterAdditionalData: {
            externalData,
            currentPageUid: uid,
          },
          footerBasic: contentstackFooter,
          footerWithSplitter: contentstackFooterWithSplitter,
          footerSplitterAdditionalData: {
            externalData,
            currentPageUid: uid,
          },
          pageUid: uid,
          locale,
          brand,
          className: [BASE_CLASS, ...categoryClasses].join(' '),
          infoBar: {
            ...contentstackInfoBar,
          },
          fullPath,
          profile,
        }}
      >
        {bazaarvoiceUrl ? (
          <BazaarVoice
            url={bazaarvoiceUrl}
            productData={productData}
            productId={productData[bazaarvoiceProductFieldName]}
            brand={brand}
            siteUrl={siteUrl}
            lang={locale}
          />
        ) : null}
        {isPriceSpiderAvailable ? (
          <PriceSpider
            priceSpider={priceSpider as PriceSpiderModel}
            lang={locale}
          />
        ) : null}
        {isAdimoAvailable ? <Adimo url={adimo!.url!} /> : null}
        {isMikMakV1Available || isMikMakV2Available ? (
          <MikMak appId={mikmak?.appId!} />
        ) : null}
        {productPageHeaderData ? (
          <ProductPageHeader {...productPageHeaderData} />
        ) : null}
        <BodyRender
          items={body}
          externalData={externalData}
          currentPageUid={uid}
        />
      </Layout>
    </RedirectsProvider>
  );
};

export const query = graphql`
  query ProductPage(
    $id: String
    $locale: String
    $brand: [String]
    $jsonRTEImgUids: [String]
  ) {
    profile: contentstackProfile {
      ...ProfileFragment
    }
    contentstackL2ProductPage(
      id: { eq: $id }
      publish_details: { locale: { eq: $locale } }
    ) {
      updated_at
      publish_details {
        time
      }
      seo {
        ...SeoFragment
      }
      openGraph: open_graph {
        ...OpenGraphFragment
      }
      body {
        ...BodyFragment
      }
      ...L2ProductPageFragment
      variantSelector: variant_selector {
        variants {
          ...L2ProductPageFragment
          ...L2ProductPageCutOutSplitterFragment
        }
      }
      categories {
        title
      }
    }
    allContentstackAssets(filter: { uid: { in: $jsonRTEImgUids } }) {
      nodes {
        uid
        url
        gatsbyImageData
      }
    }
    contentstackHeader(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...HeaderBasicFragment
    }
    contentstackHeaderWithSplitter(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...HeaderWithSplitterFragment
    }
    contentstackSettings(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...SettingsFragment
    }
    contentstackInfoBar(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...InfoBarFragment
    }
    contentstackFooter(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...FooterBasicFragment
    }
    contentstackFooterWithSplitter(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...FooterWithSplitterFragment
    }
  }
`;

export default withContentstackPreview(ProductPage, 'productpage');

export { Head } from '~/common/StructuredData';
